/**
 * Launcher V2 PR B U1 — the frontend field catalog for the Qualify step's
 * Audience Rule editor (mounted on the real `FilterPopover`, plan Key
 * Decision 1 / R1/R7). Originally shipped by A2 U5 as a small, deliberately
 * incomplete 8-field catalog (`CAMPAIGN_MEMBER_QUALIFY_FILTER_FIELDS`) with
 * its own header comment scoping the full catalog + `filterGroupsToSql`
 * wiring to PR B — this is that full catalog.
 *
 * `launcher_sfdc_campaign_members` is a raw-SQL table with NO Prisma model
 * (verified: `prisma/schema/launcher.prisma` only carries the A1 v2 models —
 * `PlaybooksSegmentation`/`Sequence`/`Playbook`/`Play`/etc. — nothing for the
 * legacy SFDC mirror tables). Every field below is cross-checked against:
 *   1. `prisma/schema/migrations/20260317_launcher_sfdc_zz_campaign_members/migration.sql`
 *      — the table's original `CREATE TABLE` (contact/account core columns).
 *   2. The later `ALTER TABLE` migrations that added the remaining columns
 *      below (partner_type, suppression fields, ARR/renewal fields,
 *      qualification/opportunity fields, GTME/GS email dates,
 *      company_segment/persona_tier/is_requalified — see
 *      `git grep launcher_sfdc_campaign_members prisma/schema/migrations`).
 *   3. `src/services/launcher-member-sync.ts`'s own INSERT column list
 *      (line ~393), the authoritative "these columns are actually
 *      populated" source — a column that exists in a migration but isn't in
 *      the sync's write list would be permanently NULL and a useless filter
 *      field.
 *
 * `key` values match the RAW COLUMN NAME 1:1 (not the differently-named
 * `CampaignMember` TS fields some of these get renamed to in
 * `mini-apps/launcher/components/types.ts`) — the backend's
 * `CAMPAIGN_MEMBER_FIELD_MAP` (`campaign-member-field-map.ts`) reads these
 * keys straight into a `$queryRaw` column reference, so a field whose key
 * doesn't match the column would silently 500 or, worse, silently match the
 * wrong column. `__tests__/campaign-member-field-map.test.ts` pins this
 * file's key set against that map's key set so the two can't silently drift.
 *
 * QA round 8, U4 (item 4) — six fields below (cm_status, sales_tier,
 * account_fit, partner_type, product_tier, plan_interval) gain enumerated
 * `values` from the shared `_shared/campaign-member-picklists.ts` lists,
 * promoted out of Launcher's `waterfallTypes.ts` and Playhouse's
 * `PlayhousePage.tsx` (both already had their own copies). `type` stays
 * `'text'` for all six on purpose.
 *
 * QA round 8 Codex fixes, F12/F14 — all six also carry `allowCustomValue:
 * true`. The lists above are hand-maintained mirrors of Salesforce picklists
 * Terra does not sync (the shared module's own hardcoded-list-smell note),
 * and they are demonstrably incomplete — Launcher's own DEFAULT `cm_status`
 * ('Invited') was missing from `CM_STATUS_VALUES` until this same round. Set
 * `values` alone would have made every one of these six fields a CLOSED
 * list: a rep's already-saved `cm_status equals Invited` would 400 on Apply,
 * and `contains`/`not_contains` (real operators for `type: 'text'`) would
 * render a closed `<select>` instead of the substring input a rep actually
 * needs. `allowCustomValue` fixes both — `valueStringsOf` (`filter-fields.ts`)
 * skips the membership check everywhere it's consulted (client draft, both
 * server gates), and `FilterPopover.tsx` renders free text with the values
 * offered as `<datalist>` suggestions rather than a closed select, for EVERY
 * operator on these six fields. The other text fields in this catalog
 * (contact_stage, persona, persona_tier, job_level, qualification_status,
 * company_segment, named_account_status, account_type,
 * last_opportunity_stage) have NO known value set in Launcher or Playhouse
 * either — they stay plain free text (no `values`, no `allowCustomValue`)
 * until a real value source exists (see the shared module's own header for
 * the hardcoded-list-smell note).
 */

import type { FilterFieldCatalogEntry } from '../_shared/flow/filter';
import {
  CM_STATUS_VALUES,
  SALES_TIER_VALUES,
  ACCOUNT_FIT_VALUES,
  PARTNER_TYPE_VALUES,
  PRODUCT_TIER_VALUES,
  PLAN_INTERVAL_VALUES,
} from '../_shared/campaign-member-picklists';

export const CAMPAIGN_MEMBER_FILTER_FIELDS: FilterFieldCatalogEntry[] = [
  // ── Core identity / status ──────────────────────────────────────────────
  { key: 'contact_name', label: 'Contact Name', group: 'Campaign Member', type: 'text' },
  { key: 'account_name', label: 'Account Name', group: 'Campaign Member', type: 'text' },
  { key: 'contact_title', label: 'Title', group: 'Campaign Member', type: 'text' },
  { key: 'contact_email', label: 'Contact Email', group: 'Campaign Member', type: 'text' },
  { key: 'contact_linkedin', label: 'LinkedIn URL', group: 'Campaign Member', type: 'text' },
  { key: 'cm_status', label: 'CM Status', group: 'Campaign Member', type: 'text', values: [...CM_STATUS_VALUES], allowCustomValue: true },
  { key: 'has_responded', label: 'Has Responded', group: 'Campaign Member', type: 'boolean' },
  { key: 'created_date', label: 'CM Created Date', group: 'Campaign Member', type: 'date' },
  { key: 'mailing_state', label: 'Mailing State', group: 'Campaign Member', type: 'text' },

  // ── Contact custom ───────────────────────────────────────────────────────
  { key: 'contact_stage', label: 'Contact Stage', group: 'Campaign Member', type: 'text' },
  { key: 'persona', label: 'Persona', group: 'Campaign Member', type: 'text' },
  { key: 'persona_tier', label: 'Persona Tier', group: 'Campaign Member', type: 'text' },
  { key: 'job_level', label: 'Job Level', group: 'Campaign Member', type: 'text' },
  { key: 'fit_score', label: 'Fit Score', group: 'Campaign Member', type: 'number' },
  { key: 'no_longer_at_company', label: 'No Longer at Company', group: 'Campaign Member', type: 'boolean' },
  { key: 'suppressed', label: 'Suppressed', group: 'Campaign Member', type: 'boolean' },
  { key: 'has_opted_out_of_email', label: 'Opted Out of Email', group: 'Campaign Member', type: 'boolean' },
  { key: 'do_not_call', label: 'Do Not Call', group: 'Campaign Member', type: 'boolean' },
  { key: 'qualification_status', label: 'Qualification Status', group: 'Campaign Member', type: 'text' },
  { key: 'company_segment', label: 'Company Segment', group: 'Campaign Member', type: 'text' },
  { key: 'is_requalified', label: 'Is Requalified', group: 'Campaign Member', type: 'boolean' },

  // ── Account ──────────────────────────────────────────────────────────────
  { key: 'sales_tier', label: 'Sales Tier', group: 'Campaign Member', type: 'text', values: [...SALES_TIER_VALUES], allowCustomValue: true },
  { key: 'account_fit', label: 'Account Fit', group: 'Campaign Member', type: 'text', values: [...ACCOUNT_FIT_VALUES], allowCustomValue: true },
  { key: 'named_account_status', label: 'Named Account Status', group: 'Campaign Member', type: 'text' },
  { key: 'account_type', label: 'Account Type', group: 'Campaign Member', type: 'text' },
  { key: 'account_suppressed', label: 'Account Suppressed', group: 'Campaign Member', type: 'boolean' },
  { key: 'competitor', label: 'Competitor', group: 'Campaign Member', type: 'boolean' },
  { key: 'agency', label: 'Agency', group: 'Campaign Member', type: 'boolean' },
  { key: 'partner_type', label: 'Partner Type', group: 'Campaign Member', type: 'text', values: [...PARTNER_TYPE_VALUES], allowCustomValue: true },
  { key: 'last_opportunity_stage', label: 'Last Opportunity Stage', group: 'Campaign Member', type: 'text' },
  { key: 'number_of_open_opps', label: 'Number of Open Opps', group: 'Campaign Member', type: 'number' },
  { key: 'potential_contract_value', label: 'Potential Contract Value', group: 'Campaign Member', type: 'number' },
  { key: 'arr_active_subscriptions', label: 'ARR (Active Subscriptions)', group: 'Campaign Member', type: 'number' },
  { key: 'arr_opportunity', label: 'ARR Opportunity', group: 'Campaign Member', type: 'number' },
  { key: 'open_pipeline', label: 'Open Pipeline', group: 'Campaign Member', type: 'number' },
  { key: 'product_tier', label: 'Product Tier', group: 'Campaign Member', type: 'text', values: [...PRODUCT_TIER_VALUES], allowCustomValue: true },
  { key: 'plan_interval', label: 'Plan Interval', group: 'Campaign Member', type: 'text', values: [...PLAN_INTERVAL_VALUES], allowCustomValue: true },
  { key: 'upcoming_renewal_close_date', label: 'Upcoming Renewal Close Date', group: 'Campaign Member', type: 'date' },

  // ── Engagement dates ─────────────────────────────────────────────────────
  { key: 'last_follow_up_date', label: 'Last Follow-Up Date', group: 'Campaign Member', type: 'date' },
  { key: 'last_gtme_meeting_date', label: 'Last GTME Meeting Date', group: 'Campaign Member', type: 'date' },
  { key: 'last_gs_meeting_date', label: 'Last GS Meeting Date', group: 'Campaign Member', type: 'date' },
  { key: 'last_claydr_meeting_date', label: 'Last ClayDR Meeting Date', group: 'Campaign Member', type: 'date' },
  { key: 'last_gtme_email_date', label: 'Last GTME Email Date', group: 'Campaign Member', type: 'date' },
  { key: 'last_gs_email_date', label: 'Last GS Email Date', group: 'Campaign Member', type: 'date' },

  // ── QA round 7, U8 (item 15) — Launcher field parity. 23 fields the
  // Launcher's waterfall (`waterfallTypes.ts#CONDITION_FIELDS`) offers that
  // this catalog was missing. `owner_role` is NOT ported — no bare
  // `owner_role` column exists on `launcher_sfdc_campaign_members` (only
  // `gtme_owner_role`, already a Legacy-marked Launcher field). Every key
  // below was verified against a migration and against
  // `src/services/launcher-member-sync.ts`'s INSERT column list before being
  // added (this file's header comment states that discipline).
  { key: 'contact_first_name', label: 'Contact First Name', group: 'Campaign Member', type: 'text' },
  { key: 'contact_last_name', label: 'Contact Last Name', group: 'Campaign Member', type: 'text' },
  { key: 'mailing_city', label: 'Mailing City', group: 'Campaign Member', type: 'text' },
  { key: 'suppression_reasons', label: 'Suppression Reasons', group: 'Campaign Member', type: 'text' },
  { key: 'gtme_owner_first_name', label: 'GTME Owner', group: 'Campaign Member', type: 'text' },
  { key: 'gtme_owner_email', label: 'GTME Owner Email', group: 'Campaign Member', type: 'text' },
  { key: 'gtme_meeting_link', label: 'GTME Meeting Link', group: 'Campaign Member', type: 'text' },
  { key: 'gs_owner_first_name', label: 'Growth Strategist', group: 'Campaign Member', type: 'text' },
  { key: 'gs_owner_email', label: 'Growth Strategist Email', group: 'Campaign Member', type: 'text' },
  { key: 'gs_meeting_link', label: 'GS Meeting Link', group: 'Campaign Member', type: 'text' },
  { key: 'claydr_first_name', label: 'ClayDR', group: 'Campaign Member', type: 'text' },
  { key: 'claydr_email', label: 'ClayDR Email', group: 'Campaign Member', type: 'text' },
  { key: 'claydr_meeting_link', label: 'ClayDR Meeting Link', group: 'Campaign Member', type: 'text' },
  { key: 'primary_met_with_name', label: 'Scanner Name', group: 'Campaign Member', type: 'text' },
  { key: 'primary_met_with_email', label: 'Scanner Email', group: 'Campaign Member', type: 'text' },
  { key: 'account_last_gtme_meeting_date', label: 'Account Last GTME Meeting', group: 'Campaign Member', type: 'date' },
  { key: 'account_last_gs_meeting_date', label: 'Account Last GS Meeting', group: 'Campaign Member', type: 'date' },
  { key: 'account_last_claydr_meeting_date', label: 'Account Last ClayDR Meeting', group: 'Campaign Member', type: 'date' },
  { key: 'account_suppression_reasons', label: 'Account Suppression Reasons', group: 'Campaign Member', type: 'text' },
  { key: 'last_inbound_email_date', label: 'Last Inbound Email Date', group: 'Campaign Member', type: 'date' },
  { key: 'last_inbound_form_submission_date', label: 'Last Inbound Form Submission Date', group: 'Campaign Member', type: 'date' },
  { key: 'last_contact_us_form_submission_date', label: 'Last Contact Us Form Submission Date', group: 'Campaign Member', type: 'date' },
  { key: 'last_claydr_email_date', label: 'Last ClayDR Email Date', group: 'Campaign Member', type: 'date' },
];
